'use client';

import { useGetQueryMeta } from '@unifyapps/network/hooks/useGetQueryMeta';
/* eslint-disable @typescript-eslint/no-restricted-imports -- needed here */
import type { LinkProps } from 'next/link';
/* eslint-disable @typescript-eslint/no-restricted-imports -- needed here */
import NextLink from 'next/link';
/* eslint-disable @typescript-eslint/no-restricted-imports -- needed here */
import { useParams } from 'next/navigation';
import { Link as NextViewTransitionsLink } from 'next-view-transitions';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { EMPTY_OBJECT } from '../consts/empty';
import { useClientContext } from '../context/client';
import { isE2ETestEnv } from '../utils/e2e';
import { processUrl } from './utils/processUrl';
import { useIsViewTransitionsEnabled } from './ViewTransitions';

// PREFETCH to false when running tests with webdriver,
// else use default prefetch
const PREFETCH = isE2ETestEnv() ? false : undefined;

/**
 * Hook to manage prefetching behavior based on user interactions
 * @param initialPrefetch - Initial prefetch value
 * @returns Object containing prefetch state and handlers for mouse/keyboard events
 */
function usePrefetch(initialPrefetch: LinkProps['prefetch'] = PREFETCH) {
  const [prefetch, setPrefetch] = useState<typeof initialPrefetch>(initialPrefetch);
  const prefetchTimerRef = useRef<NodeJS.Timeout | null>(null);

  // If initialPrefetch is a boolean (true/false), respect it and don't enable dynamic behavior
  const isDynamicPrefetchEnabled = initialPrefetch === undefined;

  const handleInteraction = useCallback(() => {
    if (isDynamicPrefetchEnabled && prefetch !== true) {
      if (prefetchTimerRef.current) clearTimeout(prefetchTimerRef.current);

      // add a 65ms delay before setting prefetch to true
      // this helps in avoiding unnecessary prefetches when the user is just hovering over the link to go to other adjacent links
      const timer = setTimeout(() => {
        setPrefetch(true);
      }, 65);

      prefetchTimerRef.current = timer as unknown as NodeJS.Timeout;
    }
  }, [prefetch, isDynamicPrefetchEnabled]);

  const handleInteractionEnd = useCallback(() => {
    if (isDynamicPrefetchEnabled) {
      if (prefetchTimerRef.current) clearTimeout(prefetchTimerRef.current);
      setPrefetch(initialPrefetch);
    }
  }, [isDynamicPrefetchEnabled, initialPrefetch]);

  const interactionProps = isDynamicPrefetchEnabled
    ? {
        onMouseEnter: handleInteraction,
        onFocus: handleInteraction,
        onMouseLeave: handleInteractionEnd,
        onBlur: handleInteractionEnd,
      }
    : {};

  useEffect(() => {
    return () => {
      if (prefetchTimerRef.current) clearTimeout(prefetchTimerRef.current);
    };
  }, []);

  return { prefetch, interactionProps };
}

const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(props, ref) {
  // useParams returns null for non-next App Router apps - ex. Delta Matrix
  const { projectSlug } = useParams<{ projectSlug?: string }>() ?? EMPTY_OBJECT;
  const initialPrefetch = props.prefetch ?? PREFETCH;
  const { prefetch, interactionProps } = usePrefetch(initialPrefetch);
  const clientContextValue = useClientContext();
  const queryMeta = useGetQueryMeta();
  const isViewTransitionsEnabled = useIsViewTransitionsEnabled();
  const client = clientContextValue?.client;

  const LinkComponent = isViewTransitionsEnabled ? NextViewTransitionsLink : NextLink;

  return (
    <LinkComponent
      prefetch={prefetch}
      {...interactionProps}
      {...props}
      href={processUrl(props.href as string, undefined, {
        client,
        projectSlug,
        queryMeta,
      })}
      ref={ref}
    />
  );
});

export { usePrefetch };
export default Link as typeof NextLink;
